// ── CAPR S2 — ASSESSMENT HISTORY (FR-N5 / UX-08), read from the decision ledger ───────────────
//
// ⚠ THE MAIN LIST IS THE APPROVED DECISIONS; a decision's returns are nested under it. A return and
//   the approval that followed it share one assessment cycle (`assignment_id`), so "returned once,
//   then approved" reads as one assessment with its history, not as two unrelated events.
// ⚠ A RETURN THAT WAS NEVER FOLLOWED BY AN APPROVAL IS STILL HISTORY (MI 5.2) — abandoned,
//   reassigned, or the control retired. Those are behind "Show returned", never dropped.
// ⚠ EACH DECISION READS AS IT STOOD AT APPROVAL: the control definition and the evidence basis are
//   the snapshot the ledger copied, not today's catalogue text.

import { useState } from 'react';
import type { ControlHub, HubDecision } from '@/api/types/cts-workspace.types';
import { DecisionBasis, fmtDate, reasonLabel } from './AssessmentContext';
import { SELF_APPROVED_NOTE, statusLabel } from './assessment-copy';

function ReturnLine({ d }: { d: HubDecision }) {
  return (
    <p className="mt-[4px] text-[11.5px] text-cert-warn-fg" data-testid="history-return">
      Returned {fmtDate(d.returned_at)}{d.returned_by_name ? ` by ${d.returned_by_name}` : ''} — {d.returned_reason}
    </p>
  );
}

/** Group the ledger: approved decisions with their same-cycle returns, and the returns no approval followed. */
export function groupHistory(decisions: HubDecision[]): {
  approved: { decision: HubDecision; returns: HubDecision[] }[];
  orphanReturns: HubDecision[];
} {
  const approvedCycles = new Set(decisions.filter((d) => d.outcome_kind === 'approved').map((d) => d.assignment_id));
  const approved = decisions.filter((d) => d.outcome_kind === 'approved').map((decision) => ({
    decision,
    returns: decisions.filter((r) => r.outcome_kind === 'returned' && r.assignment_id === decision.assignment_id),
  }));
  const orphanReturns = decisions.filter((r) => r.outcome_kind === 'returned' && !approvedCycles.has(r.assignment_id));
  return { approved, orphanReturns };
}

export function AssessmentHistory({ hub }: { hub: ControlHub }) {
  const [showReturned, setShowReturned] = useState(false);
  const { approved, orphanReturns } = groupHistory(hub.decisions);

  if (approved.length === 0 && orphanReturns.length === 0) {
    return (
      <p className="px-[16px] py-[18px] text-[13px] italic text-text-faint">
        This control has not been assessed yet, so there is no history to show.
      </p>
    );
  }

  return (
    <div data-testid="assessment-history">
      {approved.length === 0 && (
        <p className="px-[16px] py-[12px] text-[12.5px] italic text-text-faint">No assessment of this control has been approved yet.</p>
      )}
      <ul>
        {approved.map(({ decision: d, returns }) => (
          <li key={d.id} className="border-b border-border-subtle px-[16px] py-[12px] last:border-b-0" data-testid="history-decision">
            <div className="flex flex-wrap items-baseline gap-[10px]">
              <span className="text-[13px] font-medium text-text-primary">{statusLabel(d.implementation_status)}</span>
              {d.prior_implementation_status !== d.implementation_status && (
                <span className="text-[11px] text-text-secondary">from {statusLabel(d.prior_implementation_status)}</span>
              )}
              <span className="ml-auto text-[11px] text-text-secondary">Approved {fmtDate(d.approved_at)}</span>
            </div>
            <p className="mt-[3px] text-[11.5px] text-text-secondary">
              Assessed by {d.assessed_by_name ?? 'an unknown user'} · approved by {d.approver_name ?? 'an unknown user'}
              {d.self_approved ? ` · ${SELF_APPROVED_NOTE}` : ''}
              {reasonLabel(d.reason_started) ? ` · started for: ${reasonLabel(d.reason_started)}` : ''}
            </p>
            {d.rationale && <p className="mt-[4px] whitespace-pre-wrap text-[12px] text-text-primary">{d.rationale}</p>}
            {d.control_definition_snapshot?.title && (
              <p className="mt-[4px] text-[11px] text-text-faint" data-testid="history-snapshot">
                Assessed against: {d.control_definition_snapshot.title}
                {d.control_definition_snapshot.catalogue_version != null ? ` (version ${d.control_definition_snapshot.catalogue_version})` : ''}
              </p>
            )}
            <DecisionBasis decision={d} />
            {returns.map((r) => <ReturnLine key={r.id} d={r} />)}
          </li>
        ))}
      </ul>
      {orphanReturns.length > 0 && (
        <div className="border-t border-border-subtle px-[16px] py-[9px]">
          <button type="button" onClick={() => setShowReturned((v) => !v)}
            className="text-[11.5px] text-text-secondary hover:text-text-primary" data-testid="history-show-returned">
            {showReturned ? 'Hide' : 'Show'} returned assessments that were not approved ({orphanReturns.length})
          </button>
          {showReturned && orphanReturns.map((r) => (
            <div key={r.id} className="mt-[6px]">
              <p className="text-[11.5px] text-text-secondary">Assessed by {r.assessed_by_name ?? 'an unknown user'}</p>
              <ReturnLine d={r} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
